import type { LogoutOptions, RedirectLoginOptions } from "@auth0/auth0-react";
import { config } from "../config";
import { isRealNativePlatform } from "./platform";

/**
 * Native (iOS) Auth0 wiring.
 *
 * On the web, login is a normal same-origin redirect. On native we must:
 *   1. open the Auth0 login/logout page in the **system browser** — Google
 *      refuses OAuth inside embedded webviews (guideline-safe + actually works),
 *   2. use a **custom-scheme** callback so Auth0 can deep-link back into the app.
 *
 * Everything here is gated on {@link isRealNativePlatform} so `VITE_FORCE_NATIVE`
 * browser dev keeps using the ordinary SPA login flow.
 *
 * Requires an Auth0 **Native** application whose Allowed Callback + Logout URLs
 * include {@link nativeCallbackUri} (plan prerequisite P1).
 */

/**
 * Must match the Capacitor appId / CFBundleURLScheme of the native shell this
 * bundle was built for. Baked per iOS build via VITE_NATIVE_APP_ID so the dev
 * (`ai.cohesive.mobile.dev`) and prod (`ai.cohesive.mobile`) archives each emit
 * a callback scheme Auth0 accepts. Falls back to the prod id off-native.
 */
export const APP_ID = config.native.appId;

/** Auth0 domain with any scheme/trailing slash stripped (e.g. auth.local.cohesive.ai). */
function auth0Host(): string {
  return config.auth0.domain.replace(/^https?:\/\//, "").replace(/\/+$/, "");
}

/**
 * Custom-scheme callback registered in Info.plist and on the Auth0 Native app:
 *   ai.cohesive.mobile://{auth0-domain}/capacitor/ai.cohesive.mobile/callback
 */
export function nativeCallbackUri(): string {
  return `${APP_ID}://${auth0Host()}/capacitor/${APP_ID}/callback`;
}

/** returnTo for logout: the custom-scheme URL on native, the web origin otherwise. */
export function logoutReturnTo(): string {
  return isRealNativePlatform() ? nativeCallbackUri() : window.location.origin;
}

/** Open an Auth0 URL in the system browser (in-app Safari sheet, same window). */
async function openAuthUrl(url: string): Promise<void> {
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url, windowName: "_self" });
}

/**
 * Extra `loginWithRedirect` options for native — routes the authorize request
 * through the system browser. Returns `{}` on web so it can be spread safely
 * into any existing login call: `loginWithRedirect({ ...opts, ...nativeLoginOpts() })`.
 */
export function nativeLoginOpts(): RedirectLoginOptions {
  if (!isRealNativePlatform()) return {};
  return { openUrl: openAuthUrl };
}

/** Extra `logout` options for native — routes the logout through the system browser. */
export function nativeLogoutOpts(): Partial<LogoutOptions> {
  if (!isRealNativePlatform()) return {};
  return { openUrl: openAuthUrl };
}
